import React, { forwardRef, MouseEvent, useContext } from 'react';
import classNames from 'classnames';
import {
  checkCanSendNda,
  formatWithLimit,
  getEmailDomain,
  getIsUserVerified,
  getUserFullName,
  normalizeEmail,
} from 'utils';
import usePastTime from 'hooks/usePastTime';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import VerifiedBadge from 'components/VerifiedBadge';
import ChatIcon from 'icons/Chat.icon';
import NotesIcon from 'icons/Notes.icon';
import { Lead } from 'models/Lead';
import LeadCardTags from './components/LeadCardTags';
import MoveIcon from 'icons/Move.icon';
import IdCardIcon from 'icons/IdCard.icon';
import NewBadge from '../NewBadge';
import LeadCardTag from './components/LeadCardTag';
import { DocumentsCellComponent } from '../LeadsTableContainer/components/LeadsTableCell';
import SendNda from '../LeadsTableContainer/components/SendNda';
import ResendNda from '../LeadsTableContainer/components/ResendNda';
import ListingContext from '../../providers/Listing/Listing.context';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import { SIZE } from 'models/Size';
import LeadContext from './components/LeadContext';
import useLead from '../../hooks/useLead';

import './LeadCard.styles.scss';

type LeadCardProps = {
  className?: string;
  lead: Lead;
  isDisabled?: boolean;
  onLeadSelect: () => void;
  onMessage: () => void;
  onNotesSelect: () => void;
  onMouseDown: (e: MouseEvent<HTMLDivElement>) => void;
  onMouseUp: (e: MouseEvent<HTMLDivElement>) => void;
  updateLead: (lead: Lead) => void;
};

const LeadCard: React.ForwardRefRenderFunction<
  HTMLDivElement,
  LeadCardProps
> = (props, ref) => {
  const {
    className,
    lead,
    isDisabled,
    onMessage,
    onMouseDown,
    onMouseUp,
    onLeadSelect,
    onNotesSelect,
    updateLead,
  } = props;

  const {
    email,
    lastSeen,
    sessionId,
    confirmedInterest,
    isOfferAccepted,
    isOfferRejected,
    isInactive,
    withdrawnInterest,
    isExternal,
    isPrivateViewer,
    isRequestedPrivateViewer,
    isShareableLinkConsumer,
    signingStatus,
    unreadMessagesCount,
    unrepliedMessagesCount,
    unreadNotesCount,
    unrepliedNotesCount,
    context,
    envelopeTemplateName,
    buyer: { firstName, lastName, hasVerifiedContactNumber, hasVerifiedEmail },
  } = lead;

  const hasSession = !!sessionId && !isDisabled;

  const messageCount = unreadMessagesCount || unrepliedMessagesCount;

  const { canManageDeal } = useContext(ListingContext);

  const { getIsNew, getBackgroundColor } = useLead();

  const isNew = getIsNew(lead);

  const backgroundColor = getBackgroundColor(lead);

  const classes = classNames('lead-card', backgroundColor, className);

  const { t } = useTranslation();
  const pastTime = usePastTime();

  return (
    <div className={classes} ref={ref}>
      <div className="lead-card__content-wrapper">
        <div className="lead-card__content-wrapper__top-content">
          {!withdrawnInterest && (
            <LeadCardTags
              isExternal={isExternal}
              isPrivateViewer={isPrivateViewer}
              isRequestedPrivateViewer={isRequestedPrivateViewer}
              isShareableLinkConsumer={isShareableLinkConsumer}
              confirmedInterest={confirmedInterest}
              isOfferRejected={isOfferRejected}
              isOfferAccepted={isOfferAccepted}
              signingStatus={signingStatus}
              isInactive={isInactive}
              envelopeTemplateName={envelopeTemplateName}
            />
          )}

          {isNew && <NewBadge />}
          {withdrawnInterest && <LeadCardTag label="withdrawn" danger />}

          <div className="text-content">
            <p>{getUserFullName(firstName, lastName)}</p>
            {getIsUserVerified(hasVerifiedEmail, hasVerifiedContactNumber) && (
              <VerifiedBadge />
            )}
          </div>

          {email && (
            <p className="email">{getEmailDomain(normalizeEmail(email))}</p>
          )}
        </div>

        <DocumentsCellComponent lead={lead} />

        {!withdrawnInterest &&
          !isDisabled &&
          !isNew &&
          canManageDeal &&
          checkCanSendNda(signingStatus) && (
            <div className="nda-actions">
              <ResendNda lead={lead} />
              <SendNda lead={lead} updateLead={updateLead} />
            </div>
          )}
      </div>

      <div className="actions">
        <Button icon={IdCardIcon} onClick={onLeadSelect} />

        {hasSession && (
          <Button icon={ChatIcon} onClick={onMessage}>
            <Counter
              count={messageCount}
              isPositionAbsolute
              size={SIZE.SMALL}
              variant={
                unreadMessagesCount
                  ? COUNTER_VARIANTS.ORANGE
                  : COUNTER_VARIANTS.SILVER
              }
            >
              {formatWithLimit(messageCount)}
            </Counter>
          </Button>
        )}

        <Button icon={NotesIcon} onClick={onNotesSelect}>
          <Counter
            count={unreadNotesCount || unrepliedNotesCount}
            isPositionAbsolute
            size={SIZE.SMALL}
            variant={
              unreadNotesCount
                ? COUNTER_VARIANTS.ORANGE
                : COUNTER_VARIANTS.SILVER
            }
          />
        </Button>
      </div>

      <LeadContext context={context} />

      <div className="lead-card__seen">
        {t('lastSeenDate', { date: pastTime(lastSeen) })}
      </div>

      {!isDisabled && (
        <div
          className="dragable"
          data-dragable="true"
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
        >
          <MoveIcon />
        </div>
      )}
    </div>
  );
};

export default forwardRef(LeadCard);
